import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Accordion,
    Badge,
    Button,
    Divider,
    Group,
    Modal,
    Select,
    Stack,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import {
    createCaseWithSelfie,
    listCases,
    listCasesByFieldOfficer,
    listCasesByOrganization,
    listOrganizations,
    listUsers,
    type ListParams,
} from '../../api/resources';
import { applicantName } from '../../lib/subjectUtils';
import { DataPanel } from '../../components/DataPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { DEFAULT_PAGE_SIZE } from '../../hooks/useListQuery';
import { CASE_PRIORITY_VALUES, CASE_STATUS_VALUES, CASE_TYPE_VALUES, CaseType, POI_TYPE_VALUES } from '../../types/domain';
import type { CaseRecord } from '../../types/api';

type SubjectMode = 'none' | 'existing' | 'inline';
type PoiMode = 'none' | 'existing' | 'inline';

export function CasesPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [orgFilter, setOrgFilter] = useState<string | null>(null);
    const [officerFilter, setOfficerFilter] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [caseTypeFilter, setCaseTypeFilter] = useState<string | null>(null);
    const [fromMonth, setFromMonth] = useState('');
    const [toMonth, setToMonth] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [subjectSelfie, setSubjectSelfie] = useState<File | null>(null);

    const lookups = useAdminLookups();

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
    });
    const usersQ = useQuery({
        queryKey: ['users-field-officers'],
        queryFn: () => listUsers({ page: 1, limit: 500 }),
    });

    const form = useForm({
        initialValues: {
            organizationId: '',
            caseType: CaseType.Loan,
            caseSchemaId: '',
            externalReferenceId: '',
            priority: '',
            metadataJson: '{}',
            subjectMode: 'none' as SubjectMode,
            subjectId: '',
            subjectType: 'individual',
            subjectFullName: '',
            subjectPhone: '',
            subjectRole: 'borrower',
            poiMode: 'none' as PoiMode,
            poiId: '',
            poiType: 'residence',
            poiName: '',
            poiAddress: '',
        },
        validate: {
            organizationId: (v) => (v ? null : t('validation.organizationRequired')),
            caseType: (v) => (v.trim() ? null : t('validation.caseTypeRequired')),
            subjectId: (v, values) =>
                values.subjectMode === 'existing' && !v.trim()
                    ? t('validation.subjectIdRequired')
                    : null,
            subjectFullName: (v, values) =>
                values.subjectMode === 'inline' && !v.trim()
                    ? t('validation.subjectNameRequired')
                    : null,
            poiId: (v, values) =>
                values.poiMode === 'existing' && !v.trim() ? t('validation.poiIdRequired') : null,
            poiType: (v, values) =>
                values.poiMode === 'inline' && !v.trim() ? t('validation.poiTypeRequired') : null,
        },
    });

    const fetchCases = useMemo(() => {
        if (orgFilter) {
            return (params: ListParams) => listCasesByOrganization(orgFilter, params);
        }
        if (officerFilter) {
            return (params: ListParams) => listCasesByFieldOfficer(officerFilter, params);
        }
        return (params: ListParams) => listCases(params);
    }, [orgFilter, officerFilter]);

    const casesQ = useQuery({
        queryKey: ['cases', orgFilter, officerFilter, statusFilter, caseTypeFilter, fromMonth, toMonth, page],
        queryFn: () =>
            fetchCases({
                page,
                limit: DEFAULT_PAGE_SIZE,
                sort: JSON.stringify([{ field: 'createdAt', order: 'DESC' }]),
                ...(statusFilter ? { status: statusFilter } : {}),
                ...(caseTypeFilter ? { caseType: caseTypeFilter } : {}),
                ...(fromMonth ? { from: fromMonth } : {}),
                ...(toMonth ? { to: toMonth } : {}),
            }),
    });

    const createMutation = useMutation({
        mutationFn: () => {
            let metadata: Record<string, unknown> = {};
            try {
                metadata = JSON.parse(form.values.metadataJson) as Record<string, unknown>;
            } catch {
                throw new Error(t('features.cases.invalidMetadataJson'));
            }

            const body: Record<string, unknown> = {
                organizationId: form.values.organizationId,
                caseType: form.values.caseType.trim(),
                metadata,
            };

            if (form.values.caseSchemaId.trim()) {
                body.caseSchemaId = form.values.caseSchemaId.trim();
            }
            if (form.values.externalReferenceId.trim()) {
                body.externalReferenceId = form.values.externalReferenceId.trim();
            }
            if (form.values.priority.trim()) {
                body.priority = form.values.priority.trim();
            }

            if (form.values.subjectMode === 'existing') {
                body.subjectId = form.values.subjectId.trim();
            } else if (form.values.subjectMode === 'inline') {
                body.subject = {
                    subjectType: form.values.subjectType,
                    fullName: form.values.subjectFullName.trim(),
                    primaryPhone: form.values.subjectPhone.trim() || undefined,
                    role: form.values.subjectRole.trim() || undefined,
                };
            }

            if (form.values.poiMode === 'existing') {
                body.poiId = form.values.poiId.trim();
            } else if (form.values.poiMode === 'inline') {
                body.primaryPoi = {
                    poiType: form.values.poiType.trim(),
                    name: form.values.poiName.trim() || undefined,
                    claimedAddress: form.values.poiAddress.trim() || undefined,
                };
            }

            return createCaseWithSelfie(body, subjectSelfie);
        },
        onSuccess: (created) => {
            notifications.show({ color: 'green', message: t('features.cases.created') });
            setCreateOpen(false);
            setSubjectSelfie(null);
            form.reset();
            void queryClient.invalidateQueries({ queryKey: ['cases'] });
            navigate(`/cases/${created.id}`);
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const fieldOfficers = (usersQ.data?.data ?? []).filter((u) => !u.isPlatformAdmin);
    const orgOptions = (orgsQ.data?.data ?? []).map((o) => ({ value: o.id, label: o.name }));

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.cases.title')}
                description={t('features.cases.description')}
                actions={
                    <>
                        <Button variant="default" onClick={() => navigate('/cases/upload')}>
                            {t('features.cases.import.action')}
                        </Button>
                        <Button onClick={() => setCreateOpen(true)}>
                            {t('features.cases.createAction')}
                        </Button>
                    </>
                }
            />
            <DataPanel>
                <Group grow align="flex-end">
                    <Select
                        label={t('fields.organization')}
                        placeholder={t('common.all')}
                        clearable
                        searchable
                        data={orgOptions}
                        value={orgFilter}
                        onChange={(v) => {
                            setOrgFilter(v);
                            setPage(1);
                        }}
                    />
                    <Select
                        label={t('fields.fieldOfficer')}
                        placeholder={t('common.all')}
                        clearable
                        searchable
                        data={fieldOfficers.map((u) => ({
                            value: u.id,
                            label: `${u.firstName} ${u.lastName} (${u.email ?? u.id})`,
                        }))}
                        value={officerFilter}
                        onChange={(v) => {
                            setOfficerFilter(v);
                            setPage(1);
                        }}
                    />
                    <Select
                        label={t('fields.status')}
                        placeholder={t('common.all')}
                        clearable
                        data={CASE_STATUS_VALUES.map((status) => ({
                            value: status,
                            label: t(`caseStatuses.${status}`),
                        }))}
                        value={statusFilter}
                        onChange={(v) => {
                            setStatusFilter(v);
                            setPage(1);
                        }}
                    />
                    <Select
                        label={t('fields.caseType')}
                        placeholder={t('common.all')}
                        clearable
                        data={CASE_TYPE_VALUES.map((caseType) => ({
                            value: caseType,
                            label: t(`caseTypes.${caseType}`, { defaultValue: caseType }),
                        }))}
                        value={caseTypeFilter}
                        onChange={(v) => {
                            setCaseTypeFilter(v);
                            setPage(1);
                        }}
                    />
                    <TextInput
                        type="month"
                        label={t('fields.from')}
                        value={fromMonth}
                        onChange={(e) => {
                            setFromMonth(e.currentTarget.value);
                            setPage(1);
                        }}
                    />
                    <TextInput
                        type="month"
                        label={t('fields.to')}
                        value={toMonth}
                        onChange={(e) => {
                            setToMonth(e.currentTarget.value);
                            setPage(1);
                        }}
                    />
                </Group>
            </DataPanel>
            {selectedIds.length > 0 ? (
                <Group
                    justify="space-between"
                    p="sm"
                    px="md"
                    style={{
                        borderRadius: 'var(--mantine-radius-lg)',
                        backgroundColor: 'var(--mantine-color-default-hover)',
                    }}
                >
                    <Text size="sm" fw={600}>
                        {t('features.cases.selectedCount', { count: selectedIds.length })}
                    </Text>
                    <Group gap="sm">
                        <Button variant="subtle" size="xs" onClick={() => setSelectedIds([])}>
                            {t('common.actions.clearSelection')}
                        </Button>
                        <Button
                            size="xs"
                            onClick={() =>
                                navigate('/cases/monitoring-settings', {
                                    state: {
                                        cases: (casesQ.data?.data ?? []).filter((c) =>
                                            selectedIds.includes(c.id),
                                        ),
                                    },
                                })
                            }
                        >
                            {t('features.monitoringSettings.action')}
                        </Button>
                    </Group>
                </Group>
            ) : null}
            <PaginatedTable
                selection={{ selectedIds, onChange: setSelectedIds }}
                rowKey={(r: CaseRecord) => r.id}
                columns={[
                    {
                        key: 'org',
                        header: t('fields.organization'),
                        render: (r) => {
                            const org = lookups.resolveOrganization(r.organizationId);
                            return (
                                <EntityRef
                                    title={org.title}
                                    id={org.id}
                                    href={`/organizations?id=${r.organizationId}`}
                                />
                            );
                        },
                    },
                    {
                        key: 'type',
                        header: t('fields.case'),
                        render: (r) => (
                            <EntityRef
                                title={
                                    r.caseType
                                        ? t(`caseTypes.${r.caseType}`, { defaultValue: r.caseType })
                                        : t('entities.case')
                                }
                                id={r.id}
                                href={`/cases/${r.id}`}
                            />
                        ),
                    },
                    {
                        key: 'customer',
                        header: t('fields.subject'),
                        render: (r) => {
                            const name = applicantName(r.caseSubjects);
                            return name ?? t('common.empty');
                        },
                    },
                    {
                        key: 'status',
                        header: t('fields.status'),
                        render: (r) =>
                            r.status ? (
                                <Badge variant="light" size="sm">
                                    {t(`caseStatuses.${r.status}`, { defaultValue: r.status })}
                                </Badge>
                            ) : (
                                t('common.empty')
                            ),
                    },
                    {
                        key: 'lastObservedAt',
                        header: t('fields.lastObservedAt'),
                        render: (r) =>
                            r.lastObservedAt
                                ? new Date(r.lastObservedAt).toLocaleString()
                                : t('common.empty'),
                    },
                ]}
                rows={casesQ.data?.data ?? []}
                total={casesQ.data?.total ?? 0}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={casesQ.isLoading}
                onRowClick={(r) => navigate(`/cases/${r.id}`)}
            />
            <Modal
                opened={createOpen}
                onClose={() => {
                    setCreateOpen(false);
                    setSubjectSelfie(null);
                }}
                title={t('features.cases.createModalTitle')}
                size="lg"
            >
                <Stack>
                    <Select
                        label={t('fields.organization')}
                        required
                        searchable
                        data={orgOptions}
                        value={form.values.organizationId || null}
                        onChange={(v) => form.setFieldValue('organizationId', v ?? '')}
                        error={form.errors.organizationId}
                    />
                    <Select
                        label={t('fields.caseType')}
                        required
                        searchable
                        data={CASE_TYPE_VALUES.map((caseType) => ({
                            value: caseType,
                            label: t(`caseTypes.${caseType}`),
                        }))}
                        {...form.getInputProps('caseType')}
                    />
                    <Group grow>
                        <TextInput
                            label={t('fields.externalReference')}
                            placeholder={t('common.placeholders.externalRef')}
                            {...form.getInputProps('externalReferenceId')}
                        />
                        <Select
                            label={t('fields.priority')}
                            placeholder={t('common.none')}
                            clearable
                            data={CASE_PRIORITY_VALUES.map((priority) => ({
                                value: priority,
                                label: t(`casePriorities.${priority}`),
                            }))}
                            value={form.values.priority || null}
                            onChange={(v) => form.setFieldValue('priority', v ?? '')}
                        />
                    </Group>
                    <TextInput
                        label={t('fields.caseSchemaId')}
                        placeholder={t('common.placeholders.uuid')}
                        {...form.getInputProps('caseSchemaId')}
                    />
                    <Textarea
                        label={t('fields.metadataJson')}
                        minRows={3}
                        {...form.getInputProps('metadataJson')}
                    />
                    <Divider label={t('features.cases.subjectOptional')} labelPosition="left" />
                    <Select
                        label={t('fields.subject')}
                        data={[
                            { value: 'none', label: t('features.cases.subjectNone') },
                            { value: 'existing', label: t('features.cases.subjectExisting') },
                            { value: 'inline', label: t('features.cases.subjectInline') },
                        ]}
                        value={form.values.subjectMode}
                        onChange={(v) =>
                            form.setFieldValue('subjectMode', (v as SubjectMode) ?? 'none')
                        }
                    />
                    {form.values.subjectMode === 'existing' ? (
                        <TextInput
                            label={t('fields.subjectId')}
                            placeholder={t('common.placeholders.uuid')}
                            required
                            {...form.getInputProps('subjectId')}
                        />
                    ) : null}
                    {form.values.subjectMode === 'inline' ? (
                        <Stack gap="sm">
                            <Group grow>
                                <Select
                                    label={t('fields.subjectType')}
                                    data={[
                                        { value: 'individual', label: t('subjectTypes.individual') },
                                        { value: 'business', label: t('subjectTypes.business') },
                                    ]}
                                    {...form.getInputProps('subjectType')}
                                />
                                <TextInput
                                    label={t('fields.roleOnCase')}
                                    {...form.getInputProps('subjectRole')}
                                />
                            </Group>
                            <TextInput
                                label={t('fields.fullName')}
                                required
                                {...form.getInputProps('subjectFullName')}
                            />
                            <TextInput
                                label={t('fields.primaryPhone')}
                                {...form.getInputProps('subjectPhone')}
                            />
                            <Stack gap={4}>
                                <Text size="sm" fw={500}>
                                    {t('features.cases.subjectSelfie')}
                                </Text>
                                <Text size="xs" c="dimmed">
                                    {t('features.cases.subjectSelfieHint')}
                                </Text>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        setSubjectSelfie(e.currentTarget.files?.[0] ?? null)
                                    }
                                />
                            </Stack>
                        </Stack>
                    ) : null}
                    <Divider label={t('features.cases.poiOptional')} labelPosition="left" />
                    <Select
                        label={t('fields.poi')}
                        data={[
                            { value: 'none', label: t('common.none') },
                            { value: 'existing', label: t('features.cases.poiExisting') },
                            { value: 'inline', label: t('features.cases.poiInline') },
                        ]}
                        value={form.values.poiMode}
                        onChange={(v) => form.setFieldValue('poiMode', (v as PoiMode) ?? 'none')}
                    />
                    {form.values.poiMode === 'existing' ? (
                        <TextInput
                            label={t('fields.poiId')}
                            placeholder={t('common.placeholders.uuid')}
                            required
                            {...form.getInputProps('poiId')}
                        />
                    ) : null}
                    {form.values.poiMode === 'inline' ? (
                        <Stack gap="sm">
                            <Select
                                label={t('fields.poiType')}
                                required
                                data={POI_TYPE_VALUES.map((poiType) => ({
                                    value: poiType,
                                    label: t(`poiTypes.${poiType}`),
                                }))}
                                {...form.getInputProps('poiType')}
                            />
                            <TextInput label={t('fields.name')} {...form.getInputProps('poiName')} />
                            <TextInput label={t('fields.address')} {...form.getInputProps('poiAddress')} />
                        </Stack>
                    ) : null}
                    <Accordion variant="contained">
                        <Accordion.Item value="hint">
                            <Accordion.Control>{t('features.cases.apiNotes')}</Accordion.Control>
                            <Accordion.Panel>{t('features.cases.apiNotesBody')}</Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                    <Button
                        loading={createMutation.isPending}
                        onClick={() => {
                            const result = form.validate();
                            if (!result.hasErrors) {
                                createMutation.mutate();
                            }
                        }}
                    >
                        {t('common.actions.create')}
                    </Button>
                </Stack>
            </Modal>
        </Stack>
    );
}
